import {HalImageVirtualRef, PageDescriptor} from "@cm/hal"
import {UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {CmLogger} from "@cm/log"

export class ImageUpdateScheduler {
    constructor(
        private targetImage: HalImageVirtualRef,
        private logger: CmLogger,
    ) {
        this.targetImage.addRef("ImageUpdateScheduler")
    }

    dispose(): void {
        this._disposed = true
        const pageDescriptors = Array.from(this._updateInfoByPageDescriptor.keys())
        pageDescriptors.forEach((pageDescriptor) => this.cancelScheduledUpdate(pageDescriptor))
        this._updateInfoByPageDescriptor.clear()
        this._updateInfosByOptionsByPriority.clear()
        this.targetImage.release("ImageUpdateScheduler")
    }

    scheduleUpdate(pageDescriptors: PageDescriptor[], fn: UpdateFunction, priority: number, debugId?: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const requestInfo: RequestInfo = {
                remainingPageDescriptors: new Set(pageDescriptors),
                fn,
                debugId,
                resolveFn: resolve,
                rejectFn: reject,
            }
            pageDescriptors.forEach((pageDescriptor) => {
                const prevUpdateInfo = this._updateInfoByPageDescriptor.get(pageDescriptor)
                this.removePageDescriptor(pageDescriptor)
                const newOptions: FullUpdateOptions = {
                    fn,
                    priority,
                    mipLevel: pageDescriptor.mipLevel,
                }
                if (prevUpdateInfo) {
                    newOptions.priority = Math.max(prevUpdateInfo.options.priority, priority)
                }
                const newRequestInfos = [...(prevUpdateInfo?.requestInfos ?? []), requestInfo]
                this.addPageDescriptor(pageDescriptor, fn, newOptions, newRequestInfos)
            })
            this.startQueueProcessing()
        })
    }

    cancelScheduledUpdate(pageDescriptor: PageDescriptor): boolean {
        const updateInfo = this._updateInfoByPageDescriptor.get(pageDescriptor)
        if (!updateInfo) {
            return false
        }
        const completedRequests = this.removePageDescriptorAndGetCompletedRequests([updateInfo])
        completedRequests.forEach((completedRequest) => this.rejectRequest(completedRequest, new AbortUpdateError("Update cancelled")))
        return true
    }

    private addPageDescriptor(pageDescriptor: PageDescriptor, fn: UpdateFunction, options: FullUpdateOptions, requestInfos: RequestInfo[]): void {
        const mergedOptions = this.getMergedOptions(options)
        const updateInfo: UpdateInfo = {
            pageDescriptor,
            options: mergedOptions,
            fn,
            requestInfos,
        }
        if (this._updateInfoByPageDescriptor.has(pageDescriptor)) {
            throw new Error("Internal error: Page descriptor already has update info")
        }
        this._updateInfoByPageDescriptor.set(pageDescriptor, updateInfo)
        let updateInfosByOptions = this._updateInfosByOptionsByPriority.get(mergedOptions.priority)
        if (!updateInfosByOptions) {
            updateInfosByOptions = new Map<FullUpdateOptions, UpdateInfo[]>()
            this._updateInfosByOptionsByPriority.set(mergedOptions.priority, updateInfosByOptions)
        }
        let updateInfos = updateInfosByOptions.get(mergedOptions)
        if (!updateInfos) {
            updateInfos = []
            updateInfosByOptions.set(mergedOptions, updateInfos)
        }
        updateInfos.push(updateInfo)
    }

    private removePageDescriptor(pageDescriptor: PageDescriptor): boolean {
        const updateInfo = this._updateInfoByPageDescriptor.get(pageDescriptor)
        if (!updateInfo) {
            return false
        }
        this._updateInfoByPageDescriptor.delete(pageDescriptor)
        const updateInfosByOptions = this._updateInfosByOptionsByPriority.get(updateInfo.options.priority)
        if (!updateInfosByOptions) {
            throw new Error("Internal error: No update infos for this priority")
        }
        const updateInfos = updateInfosByOptions.get(updateInfo.options)
        if (!updateInfos) {
            throw new Error("Internal error: No update infos for these options")
        }
        const index = updateInfos.indexOf(updateInfo)
        if (index === -1) {
            throw new Error("Internal error: Update info not found in the list")
        }
        updateInfos.splice(index, 1)
        if (updateInfos.length === 0) {
            updateInfosByOptions.delete(updateInfo.options)
            if (updateInfosByOptions.size === 0) {
                this._updateInfosByOptionsByPriority.delete(updateInfo.options.priority)
            }
        }
        return true
    }

    private removePageDescriptorAndGetCompletedRequests(updateInfos: UpdateInfo[]): RequestInfo[] {
        const completedRequests: RequestInfo[] = []
        updateInfos.forEach((updateInfo) => {
            updateInfo.requestInfos.forEach((requestInfo) => {
                requestInfo.remainingPageDescriptors.delete(updateInfo.pageDescriptor)
                if (requestInfo.remainingPageDescriptors.size === 0) {
                    completedRequests.push(requestInfo)
                }
            })
        })
        const pageDescriptorsToRemove = updateInfos.map((updateInfo) => updateInfo.pageDescriptor)
        pageDescriptorsToRemove.forEach((pageDescriptor) => this.removePageDescriptor(pageDescriptor))
        return completedRequests
    }

    // Schedule a microtask to drain the queue, unless one is already pending.
    // _pendingMicrotask guards only against scheduling a duplicate pending microtask.
    // It is cleared as soon as the microtask starts executing (before fn runs), so that
    // any scheduleUpdate calls made from within fn (which suspend fn via await) can
    // schedule a new microtask that runs while fn is suspended. Because JS is
    // single-threaded, "suspended fn" and "new microtask's fn" never overlap in actual
    // execution — there is no concurrent GPU resource usage.
    private startQueueProcessing(): void {
        if (this._disposed || this._pendingMicrotask) {
            return
        }
        this._pendingMicrotask = true
        this.triggerUpdate()
    }

    private triggerUpdate() {
        queueMicrotask(async () => {
            // Clear before fn runs so that scheduleUpdate calls from within fn can
            // schedule a new microtask. The new microtask only executes after fn
            // suspends (at its next await), keeping GPU execution strictly serial.
            this._pendingMicrotask = false

            if (this._disposed) {
                return
            }

            // Find the highest-priority batch to process
            let highestPriority = -Infinity
            for (const priority of this._updateInfosByOptionsByPriority.keys()) {
                if (priority > highestPriority) highestPriority = priority
            }

            if (highestPriority === -Infinity) {
                return
            }

            const updateInfosByOptions = this._updateInfosByOptionsByPriority.get(highestPriority)!
            if (updateInfosByOptions.size === 0) {
                return
            }

            const [options, updateInfos] = updateInfosByOptions.entries().next().value! as [FullUpdateOptions, UpdateInfo[]]
            const pageDescriptorSet = new Set<PageDescriptor>()
            updateInfos.forEach((updateInfo) => pageDescriptorSet.add(updateInfo.pageDescriptor))
            const pageDescriptors = Array.from(pageDescriptorSet)
            const completedRequests = this.removePageDescriptorAndGetCompletedRequests(updateInfos)

            try {
                if (pageDescriptors.length > 0) {
                    const timeout = 10000
                    const timeoutTimer = setTimeout(() => {
                        this.logger.warn(`Image update function took too long (>${Math.round(timeout / 1000)}s)!`, completedRequests)
                    }, timeout)
                    await options.fn(this.targetImage, pageDescriptors, options)
                    clearTimeout(timeoutTimer)
                }
                completedRequests.forEach((completedRequest) => this.resolveRequest(completedRequest))
            } catch (e) {
                if (e instanceof Error && e.name === "AbortError") {
                    this.logger.trace("Image update function aborted", e)
                }
                completedRequests.forEach((completedRequest) => this.rejectRequest(completedRequest, e))
            }

            // Continue to next iteration. startQueueProcessing is a no-op if fn already
            // scheduled a microtask (e.g. via a nested scheduleUpdate call).
            if (this._updateInfosByOptionsByPriority.size > 0) {
                this.startQueueProcessing()
            }
        })
    }

    private getMergedOptions(options: FullUpdateOptions): FullUpdateOptions {
        const updateInfosByOptions = this._updateInfosByOptionsByPriority.get(options.priority)
        const matchingCurrentOptions = updateInfosByOptions
            ? Array.from(updateInfosByOptions.keys()).find(
                  (currentOptions) =>
                      currentOptions.fn === options.fn && currentOptions.mipLevel === options.mipLevel && currentOptions.priority === options.priority,
              )
            : undefined
        return matchingCurrentOptions ?? options
    }

    private resolveRequest(requestInfo: RequestInfo): void {
        requestInfo.resolveFn()
    }

    private rejectRequest(requestInfo: RequestInfo, reason?: unknown): void {
        requestInfo.rejectFn(reason)
    }

    private _disposed = false
    private _pendingMicrotask = false
    private _updateInfoByPageDescriptor = new Map<PageDescriptor, UpdateInfo>()
    private _updateInfosByOptionsByPriority = new Map<number, Map<FullUpdateOptions, UpdateInfo[]>>()
}

type FullUpdateOptions = Omit<Required<UpdateImageOptions>, "abortSignal"> & {
    fn: UpdateFunction
    mipLevel: number
}

type UpdateFunction = (targetImage: HalImageVirtualRef, pageDescriptors: PageDescriptor[], options?: UpdateImageOptions) => Promise<void>

type RequestInfo = {
    remainingPageDescriptors: Set<PageDescriptor>
    fn: UpdateFunction
    debugId: string | undefined
    resolveFn: () => void
    rejectFn: (reason?: unknown) => void
}

type UpdateInfo = {
    pageDescriptor: PageDescriptor
    options: FullUpdateOptions
    fn: UpdateFunction
    requestInfos: RequestInfo[]
}

export class AbortUpdateError extends Error {
    constructor(message: string) {
        super(message)
        this.name = "AbortUpdateError"
    }
}
